import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";

export const useCustomers = (params?: {
  limit?: number;
  skip?: number;
  search?: string;
  status?: string;
}) => {
  return useQuery({
    queryKey: ["customers", params],
    queryFn: () => apiService.getCustomers(params),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useCustomer = (id: string) => {
  return useQuery({
    queryKey: ["customer", id],
    queryFn: () => apiService.getCustomer(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useCustomerStats = () => {
  return useQuery({
    queryKey: ["customerStats"],
    queryFn: () => apiService.getCustomerStats(),
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useUpdateCustomerContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      contactData,
    }: {
      customerId: string;
      contactData: {
        email?: string;
        phonePrefix?: string;
        phoneNumber?: string;
      };
    }) => apiService.updateCustomerContact(customerId, contactData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });
};

export const useUpdateCustomerCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, country }: { customerId: string; country: string }) =>
      apiService.updateCustomerCountry(customerId, country),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });
};

export const useUpdateCustomerZipCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, zipCode }: { customerId: string; zipCode: string }) =>
      apiService.updateCustomerZipCode(customerId, zipCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });
};

export const useChangeCustomerPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      newPassword,
    }: {
      customerId: string;
      newPassword: string;
    }) => apiService.changeCustomerPassword(customerId, newPassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });
};

export const useUpdateCustomerSponsor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      sponsorId,
      sponsorType,
    }: {
      customerId: string;
      sponsorId: string;
      sponsorType: "dealer" | "sales_rep" | "office";
    }) => apiService.updateCustomerSponsor(customerId, { sponsorId, sponsorType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });
};
